import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  AccountSetupMode,
  DriverStatus,
  UserRole,
  normalizeUsername,
  type CreateDriverRequest,
  type CreateTeamMemberRequest,
} from '@kangaroo/shared';

import { isMinor } from '../../lib/driverUtils';
import { useAuthStore } from '../../store/authStore';
import { usernameAvailabilityMessage, useUsernameAvailability } from '../../hooks/useUsernameAvailability';

interface AddDriverDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateDriverRequest) => Promise<void>;
  onSubmitMember?: (payload: CreateTeamMemberRequest) => Promise<void>;
  billingImpactForChange?: (input: AccountBillingImpactInput) => AccountBillingImpact | null;
  showMemberType?: boolean;
  isFamilyTeamWorkspace?: boolean;
}

export type AccountBillingImpactInput = {
  role: UserRole;
  createsParentAccount: boolean;
  createsAdditionalParentAccount: boolean;
  createsTeamManagedDriver?: boolean;
};

export type AccountBillingImpact = {
  addedSeats: number;
  currentSeats: number;
  nextSeats: number;
  currentMonthly: string;
  nextMonthly: string;
};

const initialForm = {
  memberType: UserRole.DRIVER,
  name: '',
  email: '',
  username: '',
  setupMode: AccountSetupMode.PASSWORD,
  password: '',
  dateOfBirth: '',
  driverNumber: '',
  licenseNumber: '',
  parentName: '',
  parentEmail: '',
  parentSetupMode: AccountSetupMode.PASSWORD,
  parentPassword: '',
  additionalParentName: '',
  additionalParentEmail: '',
  additionalParentPassword: '',
  teamManaged: false,
};

const setupModeOptions = [
  { value: AccountSetupMode.PASSWORD, label: 'Set password now' },
  { value: AccountSetupMode.REGISTRATION_LINK, label: 'Create setup link' },
];

const memberTypeOptions = [
  { value: UserRole.DRIVER, label: 'Driver' },
  { value: UserRole.COACH, label: 'Coach' },
  { value: UserRole.MECHANIC, label: 'Mechanic' },
  { value: UserRole.PARENT, label: 'Parent / Guardian' },
];

const memberTypeDescriptions: Record<UserRole, string> = {
  [UserRole.TEAM_OWNER]: 'Full team administration access.',
  [UserRole.COACH]: 'Staff account for coaching notes, driver development, and telemetry review.',
  [UserRole.MECHANIC]: 'Staff account for vehicles, setup, maintenance, and data uploads.',
  [UserRole.DRIVER]: 'Creates a driver profile and login for mobile MyChron data sync.',
  [UserRole.PARENT]: 'Guardian login for linked drivers. On family teams, parents can co-manage drivers and team settings.',
};

function generatePassword(length = 14) {
  const groups = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '!@$%*?'];
  const allCharacters = groups.join('');
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);

  const required = groups.map((group, index) => group[values[index]! % group.length]);
  const remaining = Array.from(values.slice(required.length), (value) => allCharacters[value % allCharacters.length]);
  return [...required, ...remaining]
    .sort(() => crypto.getRandomValues(new Uint32Array(1))[0]! / 2 ** 32 - 0.5)
    .join('');
}

export function AddDriverDialog({ open, onClose, onSubmit, onSubmitMember, billingImpactForChange, showMemberType = false, isFamilyTeamWorkspace = false }: AddDriverDialogProps) {
  const user = useAuthStore((state) => state.user);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    username?: string;
    password?: string;
    dateOfBirth?: string;
    email?: string;
    additionalParentName?: string;
    additionalParentEmail?: string;
    additionalParentPassword?: string;
  }>({});
  const [addAnotherParent, setAddAnotherParent] = useState(false);
  const [billingConfirmed, setBillingConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const memberType = showMemberType ? form.memberType : UserRole.DRIVER;
  const isDriverAccount = memberType === UserRole.DRIVER;
  const isAddingAsParent = isDriverAccount && user?.role === UserRole.PARENT;
  const canCreateTeamManagedDriver = isDriverAccount
    && !isAddingAsParent
    && [user?.role, ...(user?.additionalRoles ?? [])].some((role) => role === UserRole.TEAM_OWNER || role === UserRole.COACH);
  const requiresParent = useMemo(() => isDriverAccount && isMinor(form.dateOfBirth), [form.dateOfBirth, isDriverAccount]);
  const parentRequired = requiresParent && !(canCreateTeamManagedDriver && form.teamManaged);
  const showParentAccountFields = parentRequired && !isAddingAsParent;
  const driverUsesPassword = isAddingAsParent || form.setupMode === AccountSetupMode.PASSWORD;
  const parentUsesPassword = form.parentSetupMode === AccountSetupMode.PASSWORD;
  const memberTypeLabel = memberTypeOptions.find((option) => option.value === memberType)?.label ?? 'Driver';
  const passwordLabel = isAddingAsParent ? 'Child password' : 'Password';
  const memberTypeDescription = isAddingAsParent
    ? 'Creates a login for your child to sync MyChron data from the mobile app. You stay linked as the parent on this account.'
    : memberTypeDescriptions[memberType];
  const usernameAvailability = useUsernameAvailability(form.username, open && isAddingAsParent);
  const usernameFeedback = usernameAvailabilityMessage(usernameAvailability);
  const usernameReady = usernameAvailability.status === 'ready' && usernameAvailability.result.available;
  const usernameBlocking = isAddingAsParent
    && form.username.trim().length > 0
    && (usernameAvailability.status === 'checking' || (usernameAvailability.status === 'ready' && !usernameAvailability.result.available));
  const availableMemberTypeOptions = useMemo(
    () => (isFamilyTeamWorkspace
      ? memberTypeOptions.filter((option) => option.value !== UserRole.COACH && option.value !== UserRole.MECHANIC)
      : memberTypeOptions),
    [isFamilyTeamWorkspace],
  );
  const billingImpact = billingImpactForChange?.({
    role: memberType,
    createsParentAccount: Boolean(isDriverAccount && showParentAccountFields),
    createsAdditionalParentAccount: Boolean(isDriverAccount && addAnotherParent && parentRequired),
    createsTeamManagedDriver: Boolean(isDriverAccount && !isFamilyTeamWorkspace && form.teamManaged),
  }) ?? null;
  const requiresBillingConfirmation = Boolean(billingImpact && billingImpact.addedSeats > 0);

  function updateField(field: keyof typeof initialForm, value: string | boolean) {
    setForm((current) => ({ ...current, [field]: value }));
    if (field === 'memberType') {
      setBillingConfirmed(false);
    }
    if (field in fieldErrors) {
      setFieldErrors((current) => ({ ...current, [field]: undefined }));
    }
    if (error) {
      setError(null);
    }
  }

  function handleClose() {
    if (submitting) {
      return;
    }
    setForm(initialForm);
    setFieldErrors({});
    setAddAnotherParent(false);
    setBillingConfirmed(false);
    setError(null);
    onClose();
  }

  function buildAdditionalParentPayload() {
    if (!addAnotherParent || !parentRequired) {
      return undefined;
    }

    return {
      name: form.additionalParentName.trim(),
      email: form.additionalParentEmail.trim(),
      setupMode: AccountSetupMode.PASSWORD,
      password: form.additionalParentPassword,
    };
  }

  function validateAdditionalParent(nextFieldErrors: typeof fieldErrors) {
    if (!addAnotherParent || !parentRequired) {
      return;
    }

    if (!form.additionalParentName.trim()) {
      nextFieldErrors.additionalParentName = 'Parent name is required';
    }
    if (!form.additionalParentEmail.trim()) {
      nextFieldErrors.additionalParentEmail = 'Parent email is required';
    } else if (isAddingAsParent && user && form.additionalParentEmail.trim().toLowerCase() === user.email.toLowerCase()) {
      nextFieldErrors.additionalParentEmail = 'Must be different from your email';
    } else if (showParentAccountFields && form.additionalParentEmail.trim().toLowerCase() === form.parentEmail.trim().toLowerCase()) {
      nextFieldErrors.additionalParentEmail = 'Must be different from the first parent email';
    }
    if (form.additionalParentPassword.length < 8) {
      nextFieldErrors.additionalParentPassword = 'Parent password must be at least 8 characters';
    }
  }

  function validateForm() {
    const nextFieldErrors: typeof fieldErrors = {};

    if (form.name.trim().length < 2) {
      nextFieldErrors.name = isAddingAsParent ? "Enter your child's full name" : 'Full name is required';
    }

    if (isAddingAsParent) {
      if (!form.username.trim()) {
        nextFieldErrors.username = 'Choose a username for your child';
      } else if (usernameBlocking) {
        nextFieldErrors.username = usernameFeedback.error
          ? usernameFeedback.text
          : 'Checking username availability…';
      } else if (!usernameReady) {
        nextFieldErrors.username = usernameFeedback.error
          ? usernameFeedback.text
          : 'Choose an available username';
      } else if (user?.username && normalizeUsername(form.username) === normalizeUsername(user.username)) {
        nextFieldErrors.username = 'Must be different from your username';
      }
    } else if (!form.email.trim()) {
      nextFieldErrors.email = 'Email is required';
    }

    if (driverUsesPassword && form.password.length < 8) {
      nextFieldErrors.password = `${passwordLabel} must be at least 8 characters`;
    }

    if (isDriverAccount && !form.dateOfBirth) {
      nextFieldErrors.dateOfBirth = 'Date of birth is required';
    }

    validateAdditionalParent(nextFieldErrors);

    return nextFieldErrors;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const nextFieldErrors = validateForm();

    if (isDriverAccount && parentRequired && showParentAccountFields) {
      if (!form.parentName.trim() || !form.parentEmail.trim()) {
        setError('Parent name and email are required for drivers under 18');
        return;
      }
      if (parentUsesPassword && form.parentPassword.length < 8) {
        setError('Parent password must be at least 8 characters');
        return;
      }
      if (form.parentEmail.trim().toLowerCase() === form.email.trim().toLowerCase()) {
        setError('Parent email must be different from the driver email');
        return;
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({});
    if (requiresBillingConfirmation && !billingConfirmed) {
      setError('Confirm the billing change before creating this account.');
      return;
    }

    setSubmitting(true);

    try {
      if (!isDriverAccount) {
        if (!onSubmitMember) {
          setError('This account type cannot be created from here');
          return;
        }

        await onSubmitMember({
          name: form.name.trim(),
          email: form.email.trim(),
          role: memberType as CreateTeamMemberRequest['role'],
          setupMode: form.setupMode,
          password: driverUsesPassword ? form.password : undefined,
        });
        setForm(initialForm);
        setBillingConfirmed(false);
        onClose();
        return;
      }

      const payload: CreateDriverRequest = {
        name: form.name.trim(),
        setupMode: form.setupMode,
        password: driverUsesPassword ? form.password : undefined,
        dateOfBirth: form.dateOfBirth,
        driverNumber: form.driverNumber.trim() || undefined,
        licenseNumber: form.licenseNumber.trim() || undefined,
        status: DriverStatus.ACTIVE,
      };

      if (isAddingAsParent) {
        payload.username = form.username.trim();
      } else {
        payload.email = form.email.trim();
      }

      if (canCreateTeamManagedDriver && requiresParent && form.teamManaged) {
        payload.teamManaged = true;
      } else if (parentRequired && isAddingAsParent && user) {
        payload.parent = {
          name: user.name,
          email: user.email,
          setupMode: AccountSetupMode.REGISTRATION_LINK,
        };
      } else if (parentRequired) {
        payload.parent = {
          name: form.parentName.trim(),
          email: form.parentEmail.trim(),
          setupMode: form.parentSetupMode,
          password: parentUsesPassword ? form.parentPassword : undefined,
        };
      }

      const additionalParent = buildAdditionalParentPayload();
      if (additionalParent?.name && additionalParent.email) {
        payload.additionalParent = additionalParent;
      }

      await onSubmit(payload);
      setForm(initialForm);
    setBillingConfirmed(false);
      onClose();
    } catch (submitError) {
      const message = submitError instanceof Error
        ? submitError.message
        : 'Unable to create driver';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520, lg: 560 },
          maxWidth: '100vw',
          bgcolor: 'background.default',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1.1, sm: 1.4 }, py: 1.05 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.2, minHeight: 40 }}>
            <Typography sx={{ fontSize: '1.02rem', fontWeight: 950, lineHeight: 1.2 }}>
              {showMemberType ? 'Create Team Member' : isAddingAsParent ? 'Add Child Driver' : 'Add Driver'}
            </Typography>
            <IconButton
              aria-label="Close drawer"
              size="small"
              onClick={handleClose}
              disabled={submitting}
              sx={{ width: 32, height: 32, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, py: 2 }}>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
              {isAddingAsParent ? 'Child account' : `${memberTypeLabel} Account`}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {memberTypeDescription}
            </Typography>
            <Grid container spacing={1.5}>
              {showMemberType && (
                <Grid item xs={12}>
                  <TextField select label="Account type" value={form.memberType} onChange={(event) => updateField('memberType', event.target.value)} fullWidth size="small">
                    {availableMemberTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </TextField>
                  {isFamilyTeamWorkspace ? (
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 1 }}>
                      Family teams can add drivers and parents only — not coaches or mechanics.
                    </Typography>
                  ) : null}
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label={isAddingAsParent ? "Child's full name" : 'Full name'}
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  required
                  fullWidth
                  size="small"
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                />
              </Grid>
              {isAddingAsParent ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Username"
                      value={form.username}
                      onChange={(event) => updateField('username', event.target.value.replace(/\s+/g, ''))}
                      required
                      fullWidth
                      size="small"
                      autoComplete="username"
                      error={Boolean(fieldErrors.username) || usernameFeedback.error}
                      helperText={fieldErrors.username ?? usernameFeedback.text}
                      FormHelperTextProps={
                        !fieldErrors.username && usernameFeedback.success
                          ? { sx: { color: 'success.main' } }
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label={passwordLabel}
                        type="text"
                        value={form.password}
                        onChange={(event) => updateField('password', event.target.value)}
                        required
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        error={Boolean(fieldErrors.password)}
                        helperText={fieldErrors.password ?? 'Minimum 8 characters'}
                      />
                      <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start', minHeight: 37, whiteSpace: 'nowrap' }} onClick={() => updateField('password', generatePassword())}>Generate</Button>
                    </Stack>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      required
                      fullWidth
                      size="small"
                      error={Boolean(fieldErrors.email)}
                      helperText={fieldErrors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Account setup" value={form.setupMode} onChange={(event) => updateField('setupMode', event.target.value)} fullWidth size="small">
                      {setupModeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  {driverUsesPassword ? (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          label={passwordLabel}
                          type="text"
                          value={form.password}
                          onChange={(event) => updateField('password', event.target.value)}
                          required
                          fullWidth
                          size="small"
                          error={Boolean(fieldErrors.password)}
                          helperText={fieldErrors.password ?? 'Minimum 8 characters'}
                        />
                        <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start', minHeight: 37, whiteSpace: 'nowrap' }} onClick={() => updateField('password', generatePassword())}>Generate</Button>
                      </Stack>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', py: 0.75 }}>
                        A setup link will be generated after the account is created.
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              {isDriverAccount && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date of birth"
                      type="date"
                      value={form.dateOfBirth}
                      onChange={(event) => updateField('dateOfBirth', event.target.value)}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.dateOfBirth)}
                      helperText={fieldErrors.dateOfBirth}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Driver number" value={form.driverNumber} onChange={(event) => updateField('driverNumber', event.target.value)} fullWidth size="small" placeholder="#67" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="License number" value={form.licenseNumber} onChange={(event) => updateField('licenseNumber', event.target.value)} fullWidth size="small" />
                  </Grid>
                  {canCreateTeamManagedDriver && requiresParent ? (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.teamManaged}
                            onChange={(event) => {
                              setForm((current) => ({
                                ...current,
                                teamManaged: event.target.checked,
                              }));
                              if (event.target.checked) {
                                setAddAnotherParent(false);
                              }
                              setBillingConfirmed(false);
                            }}
                            size="small"
                          />
                        }
                        label="Team-managed driver"
                        sx={{ alignItems: 'flex-start' }}
                      />
                    </Grid>
                  ) : null}
                </>
              )}
            </Grid>

            {isAddingAsParent && parentRequired && user && (
              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                This driver will be linked to your parent account ({user.name} · {user.email}).
              </Alert>
            )}

            {showParentAccountFields && (
              <>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', pt: 0.5 }}>
                  Parent / Guardian Account
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  Drivers under 18 require a parent account with a separate login email.
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField label="Parent name" value={form.parentName} onChange={(event) => updateField('parentName', event.target.value)} required fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Parent email" type="email" value={form.parentEmail} onChange={(event) => updateField('parentEmail', event.target.value)} required fullWidth size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Parent setup" value={form.parentSetupMode} onChange={(event) => updateField('parentSetupMode', event.target.value)} fullWidth size="small">
                      {setupModeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                  {parentUsesPassword ? (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1}>
                        <TextField label="Parent password" type="text" value={form.parentPassword} onChange={(event) => updateField('parentPassword', event.target.value)} required fullWidth size="small" />
                        <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start', minHeight: 37, whiteSpace: 'nowrap' }} onClick={() => updateField('parentPassword', generatePassword())}>Generate</Button>
                      </Stack>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', py: 0.75 }}>
                        A setup link will be generated for the parent account.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            {(isAddingAsParent || showParentAccountFields) && parentRequired ? (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addAnotherParent}
                      onChange={(event) => {
                        setAddAnotherParent(event.target.checked);
                        setBillingConfirmed(false);
                        if (!event.target.checked) {
                          setFieldErrors((current) => ({
                            ...current,
                            additionalParentName: undefined,
                            additionalParentEmail: undefined,
                            additionalParentPassword: undefined,
                          }));
                        }
                      }}
                      size="small"
                    />
                  }
                  label="Add another parent / guardian"
                  sx={{ alignItems: 'flex-start', mb: addAnotherParent ? 1.5 : 0 }}
                />
                {addAnotherParent ? (
                  <>
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mb: 1.5 }}>
                      The second parent gets their own login and can co-manage your family team on web and mobile.
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          label="Parent name"
                          value={form.additionalParentName}
                          onChange={(event) => updateField('additionalParentName', event.target.value)}
                          required
                          fullWidth
                          size="small"
                          error={Boolean(fieldErrors.additionalParentName)}
                          helperText={fieldErrors.additionalParentName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Parent email"
                          type="email"
                          value={form.additionalParentEmail}
                          onChange={(event) => updateField('additionalParentEmail', event.target.value)}
                          required
                          fullWidth
                          size="small"
                          error={Boolean(fieldErrors.additionalParentEmail)}
                          helperText={fieldErrors.additionalParentEmail}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            label="Parent password"
                            type="text"
                            value={form.additionalParentPassword}
                            onChange={(event) => updateField('additionalParentPassword', event.target.value)}
                            required
                            fullWidth
                            size="small"
                            error={Boolean(fieldErrors.additionalParentPassword)}
                            helperText={fieldErrors.additionalParentPassword ?? 'Minimum 8 characters'}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ alignSelf: 'flex-start', minHeight: 37, whiteSpace: 'nowrap' }}
                            onClick={() => updateField('additionalParentPassword', generatePassword())}
                          >
                            Generate
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        </Box>

        {billingImpact ? (
          <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
            <Alert severity={billingImpact.addedSeats > 0 ? 'info' : 'success'} sx={{ fontSize: '0.84rem' }}>
              Adds {billingImpact.addedSeats} seat{billingImpact.addedSeats === 1 ? '' : 's'}. Monthly estimate: {billingImpact.currentMonthly} → {billingImpact.nextMonthly}.
            </Alert>
            {requiresBillingConfirmation ? (
              <FormControlLabel
                control={<Checkbox checked={billingConfirmed} onChange={(event) => setBillingConfirmed(event.target.checked)} />}
                label="I understand this updates billing when the account is created."
                sx={{ mt: 0.75, '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } }}
              />
            ) : null}
          </Box>
        ) : null}

        <Box sx={{ flexShrink: 0, borderTop: billingImpact ? 0 : '1px solid', borderColor: 'divider', px: { xs: 1.5, sm: 2 }, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting || (isAddingAsParent && usernameBlocking) || (requiresBillingConfirmation && !billingConfirmed)}>
            {submitting ? 'Creating…' : showMemberType ? 'Create Account' : isAddingAsParent ? 'Add Child Driver' : 'Add Driver'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
